import logoLockup from '@/assets/logo-cloud.png';
import logoLockupReversed from '@/assets/logo-cloud-reversed.png';
import logoMark from '@/assets/logo-mark.png';

/**
 * The brand assets, in one place so every placement stays in step.
 *
 * Three variants, each with a job:
 *  • `<LogoLockup>`            — compass + "ORDER PILOT CLOUD". Needs room to be
 *                                legible, so it is for sign-in screens, not 32px
 *                                chrome.
 *  • `<LogoLockup reversed />`  — the light-on-navy artwork, for the navy brand
 *                                panel on the sign-in pages. Its own background
 *                                is exactly `bg-navy` (#0F2E50), so on that panel
 *                                it reads as if it had a transparent one.
 *  • `<LogoMark>`              — the compass alone, for the sidebar and mobile
 *                                bar where the wordmark would be unreadable and
 *                                is already beside it as text.
 *
 * CALLER BEWARE: the lockup sizes itself from `h-*` plus `w-auto`. Drop it into
 * a column flex container and the default `align-items: stretch` will pull that
 * auto width out to the full container, squashing the logo flat. Pass
 * `self-start` (or centre it explicitly) in any flex parent.
 *
 * The mark has no alpha channel (a solid off-white plate), so it is rendered
 * inside a rounded, clipped tile — otherwise a pale square shows against the
 * white sidebar. That also makes it read as an app icon rather than a stray
 * image.
 */

interface LogoProps {
  className?: string;
  /** Light-on-navy artwork. Only correct on a `bg-navy` surface. */
  reversed?: boolean;
}

export function LogoLockup({ className = 'h-16', reversed = false }: LogoProps) {
  return (
    <img
      src={reversed ? logoLockupReversed : logoLockup}
      // Decorative here: every screen that shows it also states the product name
      // in adjacent text, so announcing it again is just noise for a screen reader.
      alt=""
      aria-hidden="true"
      className={`${className} w-auto select-none`}
      draggable={false}
    />
  );
}

export function LogoMark({
  className = 'h-8 w-8',
  src,
}: {
  className?: string;
  /** A tenant's own logo, when one is set — falls back to the compass mark. */
  src?: string | null;
}) {
  return (
    <span
      aria-hidden="true"
      className={`${className} shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white`}
    >
      <img src={src ?? logoMark} alt="" className="h-full w-full object-cover" draggable={false} />
    </span>
  );
}
